import { MaskIcon } from '@/components/ui/MaskIcon';

import { elementFor } from '../elements/registry';

/**
 * The list's "Features" column — the element types a questionnaire uses, as icons.
 *
 * Six shown, the rest as "+N more". Three was too eager: most questionnaires use four or five element
 * types, so the common case collapsed to "three icons and +2 more" — hiding information the column
 * exists to show while spending the same width. Six covers nearly every real form outright, and the
 * cap still exists because a form with twelve types would push the table into a horizontal scroll.
 *
 * Icons rather than names on purpose — at a glance the reader is asking "is this a short form or a
 * document-heavy one", and a row of glyphs answers that faster than a comma-separated list. The
 * full name is on each icon's tooltip, and the overflow badge names what it is hiding.
 */
const VISIBLE = 6;

export function QuestionnaireFeatureIcons({ fieldTypes }: { fieldTypes: string[] }) {
  if (fieldTypes.length === 0) {
    return <span className="text-[length:var(--text-14)] text-placeholder">No fields yet</span>;
  }

  const shown = fieldTypes.slice(0, VISIBLE);
  const hidden = fieldTypes.slice(VISIBLE);

  return (
    <div className="flex items-center gap-2">
      {shown.map((fieldType, index) => {
        const element = elementFor(fieldType);
        return (
          <span key={fieldType} className="flex items-center gap-2">
            <MaskIcon src={element.icon} className="h-4 w-4 text-gray66" label={element.label} />
            {/* An arrow between icons, as the designer has it — the strip reads as a sequence,
                which is why the types are sent in journey order rather than alphabetically. */}
            {index < shown.length - 1 && (
              <span aria-hidden className="text-[12px] leading-none text-[#9CA3AF]">
                →
              </span>
            )}
          </span>
        );
      })}

      {hidden.length > 0 && (
        <span
          className="text-[length:var(--text-12)] font-medium text-[#6B7280]"
          // Names what is hidden, so the badge is informative rather than just a count.
          title={hidden.map((t) => elementFor(t).label).join(', ')}
        >
          +{hidden.length} more
        </span>
      )}
    </div>
  );
}
